import {
  championshipLeagues,
  UPCOMING_GAMES_ITEM,
  UPCOMING_GAMES_LABEL,
  ROUTES,
  TODAYS_GAMES_ITEM,
  TODAYS_GAMES_LABEL,
  LIVE_EVENTS_ITEM,
  LIVE_EVENTS_LABEL,
  LOCAL_STORAGE_KEY,
} from 'constants';
import {
  CHOSEN_TEAM,
  CHOSEN_LEAGUE,
  DEFAULT_EVENTS_FEED,
} from 'constants/localStorageKey';
import React, { useCallback, useMemo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { createSelectedValue, isEmptyObject } from 'helpers';
import { safeLogEvent } from 'firebase.js';
import { allTeamsResetAction } from 'redux/teams/actionCreators.js';
import {
  allGamesResetAction,
  getAllGamesAction,
} from 'redux/games/actionCreators.js';
import {
  getTeamAppAction,
  clearTeamAppAction,
} from 'redux/teamApp/actionCreators.js';
import useLocalStorage, {
  useGetSortedLeaguesList,
  useGetCollegeLeagues,
} from 'hooks';
import { api } from 'api/api';
import types from 'redux/types';
import getIndex from 'helpers/getIndex';

import { Selector, AddToFavorite } from 'components';

import { SelectContainer, StyledFlex } from './style';

export const Header = ({ setEventLeague }) => {
  const {
    data: configuration,
    loading: isConfigurationLoading,
    error: isConfigurationError,
  } = useSelector((state) => state.configuration);
  const { data: leagues } = useSelector((state) => state.leagues);
  const { data: teams } = useSelector((state) => state.teams);
  const { data: liveGames } = useSelector((state) => state.liveGames);
  const isUserAuth = !!useSelector((state) => state.user.data.token);
  const userId = useSelector((state) => state.user.data.user_id);
  const favoriteTeams = useSelector((state) => state.favoriteTeams.data);

  const [searchParams, setSearchParams] = useSearchParams();
  // eslint-disable-next-line no-unused-vars
  const [savedTeam, setSavedTeam] = useLocalStorage(CHOSEN_TEAM, '');
  // eslint-disable-next-line no-unused-vars
  const [savedLeague, setSavedLeague] = useLocalStorage(CHOSEN_LEAGUE, '');
  const [defaultEventsFeed, setDefaultEventsFeed] = useLocalStorage(
    DEFAULT_EVENTS_FEED,
    LIVE_EVENTS_LABEL
  );

  const params = Object.fromEntries([...searchParams]);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { leaguesList } = useGetSortedLeaguesList(leagues || []);

  // Determine the correct dropdown value based on URL params or defaultEventsFeed
  const getDefaultLeagueValue = useMemo(() => {
    if (params?.leagueId) {
      // If URL params exist, use them
      return createSelectedValue({
        value: params.leagueId,
        label: params.league,
      });
    }

    // When no URL params, find the matching option from leaguesList based on defaultEventsFeed
    // This ensures the dropdown value always exists in the options array
    const matchingOption = leaguesList.find(
      (option) => option.label === defaultEventsFeed
    );

    // Don't select Live Events if it's currently disabled (no live games) -
    // MainPage falls back to rendering Today's Events in that case, so the
    // dropdown must show the same thing it's actually displaying.
    if (matchingOption && !matchingOption.isDisabled) {
      return createSelectedValue({
        value: matchingOption.value,
        label: matchingOption.label,
      });
    }

    // Fallback to Today's Events if no match found (e.g., if Live Events is disabled)
    return createSelectedValue({
      value: TODAYS_GAMES_ITEM.value,
      label: TODAYS_GAMES_LABEL,
    });
  }, [params?.leagueId, params?.league, leaguesList]);

  const allTeams = useMemo(() => {
    if (!!teams && !!teams.length && teams[0].team_name) {
      return teams;
    } else {
      return [];
    }
  }, [teams]);

  const handleLeaguesChange = useCallback(
    (selected) => {
      setEventLeague('');
      setSavedTeam('');
      const { value, label } = selected;
      safeLogEvent('select_content', {
        content_type: 'Changed League',
        content_id: label,
      });

      if (
        championshipLeagues.includes(label) ||
        label === UPCOMING_GAMES_LABEL
      ) {
        dispatch(allTeamsResetAction());
        dispatch(allGamesResetAction());
        dispatch(clearTeamAppAction());
        setSavedLeague('');
        setEventLeague('');
      }

      if (championshipLeagues.includes(label)) {
        setTimeout(() => {
          navigate(
            `/${ROUTES.CHAMPIONSHIPS}?league=${label}&leagueId=${value}`
          );
        }, 500);
      } else if (label === UPCOMING_GAMES_LABEL) {
        setTimeout(() => {
          navigate(`/${ROUTES.UPCOMING_GAMES}`);
        }, 500);
      } else if (label === TODAYS_GAMES_LABEL || label === LIVE_EVENTS_LABEL) {
        dispatch(allGamesResetAction());
        dispatch(clearTeamAppAction());
        setSearchParams([]);
        setSavedTeam('');
        setSavedLeague('');
        setDefaultEventsFeed(label);
      } else {
        // Clear team app data when league changes
        dispatch(clearTeamAppAction());
        setSearchParams(
          (prevValue) => {
            if (params?.team && params?.teamId) {
              prevValue.delete('team');
              prevValue.delete('teamId');
              prevValue.delete('eventId');
            }
            prevValue.set('league', label);
            prevValue.set('leagueId', value);
            return prevValue;
          },
          { replace: true }
        );
      }
    },
    [
      setSavedTeam,
      dispatch,
      setSavedLeague,
      navigate,
      setSearchParams,
      params?.team,
      params?.teamId,
      setEventLeague,
    ]
  );

  const handleTeamChange = useCallback(
    async (selected) => {
      dispatch(allGamesResetAction());
      const { value, label } = selected;
      const teamId = value;
      const currentLeague = params?.league;

      if (label === UPCOMING_GAMES_LABEL) {
        // Clear team app data when showing upcoming games
        dispatch(clearTeamAppAction());
        // Note: getUpcomingGames is called by DashboardUpcomingGames via React Query hook
        // which provides caching and prevents duplicate requests
      } else {
        // Fetch team app data when a specific team is selected
        dispatch(getTeamAppAction(value));
      }

      // If selecting from Favorites, we need to find the actual league for this team
      let actualLeague = currentLeague;
      let actualLeagueId = params?.leagueId;

      if (currentLeague === 'Favorites' && leagues?.length > 0) {
        // Search through all leagues to find which one contains this team
        for (const league of leagues) {
          try {
            const { data: leagueTeams } = await api.teams.getTeams(
              league.league_id
            );
            const teamExists = leagueTeams?.some(
              (t) => t.team_id.toString() === teamId.toString()
            );
            if (teamExists) {
              actualLeague = league.league_name;
              actualLeagueId = league.league_id;
              break;
            }
          } catch (error) {
            // Continue searching other leagues
            continue;
          }
        }
      }

      setSearchParams(
        (prevValue) => {
          // Always delete eventId when changing teams
          prevValue.delete('eventId');
          prevValue.set('team', label);
          prevValue.set('teamId', value);
          // Update league to actual league instead of "Favorites"
          if (actualLeague && actualLeague !== 'Favorites') {
            prevValue.set('league', actualLeague);
            prevValue.set('leagueId', actualLeagueId);
          }
          return prevValue;
        },
        { replace: true }
      );

      setSavedTeam({ team: label, teamId: value });

      safeLogEvent('select_content', {
        content_type: 'Changed Team',
        content_id: label,
      });
    },
    [
      dispatch,
      setSearchParams,
      setSavedTeam,
      params?.league,
      params?.leagueId,
      leagues,
    ]
  );

  const handleCollegeLeagueChanged = useCallback(
    (selected) => {
      setSavedTeam('');
      const { value, label } = selected;

      safeLogEvent('select_content', {
        content_type: 'Changed College League',
        content_id: label,
      });

      // Save the selected college league to localStorage
      const lastCollegeLeagueKey = `${LOCAL_STORAGE_KEY}last-college-league`;
      localStorage.setItem(
        lastCollegeLeagueKey,
        JSON.stringify({ label, value })
      );

      // Update URL params - the useEffect at line 470 will handle loading the data
      // This prevents duplicate API calls
      setSearchParams(
        (prevValue) => {
          if (params?.team && params?.teamId) {
            prevValue.delete('team');
            prevValue.delete('teamId');
            prevValue.delete('eventId');
          }
          prevValue.set('collegeLeague', label);
          prevValue.set('collegeLeagueId', value);
          return prevValue;
        },
        { replace: true }
      );
    },
    [params?.team, params?.teamId, setSavedTeam, setSearchParams]
  );

  const teamsOptionsList = useMemo(() => {
    let teamsList = [];

    if (params?.league === 'Favorites' && Boolean(favoriteTeams?.length)) {
      teamsList = favoriteTeams?.map((item) => {
        // Include league info in the label for display, but keep value as team_id
        const leagueName = item.favourite_league || '';
        const displayLabel = leagueName
          ? `${item.favourite_teamLabel} (${leagueName})`
          : item.favourite_teamLabel;

        return {
          label: item.favourite_teamLabel, // Keep original for matching
          displayLabel: displayLabel, // For visual display
          value: item.favourite_team_id.toString(),
          league: leagueName, // Store league for sport icon detection
        };
      });
      return teamsList;
    } else {
      teamsList = allTeams?.map((item) => ({
        label: item.team_name,
        value: item.team_id.toString(),
      }));

      if (
        configuration?.display_teams_select === '1' ||
        configuration?.display_teams_select === 1
      ) {
        const upcomingGamesOption = {
          label: UPCOMING_GAMES_LABEL,
          value: configuration?.one_league_id,
        };

        return [upcomingGamesOption, ...teamsList];
      }

      return teamsList;
    }
  }, [
    params?.league,
    favoriteTeams,
    allTeams,
    configuration?.display_teams_select,
    configuration?.one_league_id,
  ]);

  const isCollegeLeagues =
    (configuration?.display_one_team === '1' ||
      configuration?.display_one_team === 1) &&
    configuration?.sport_level === 'Collegiate';

  const { data: collegeLeagues, isLoading: isCollegeLeaguesLoading } =
    useGetCollegeLeagues(isCollegeLeagues);

  let collegeLeaguesList = [
    UPCOMING_GAMES_ITEM,
    ...((collegeLeagues?.data ? collegeLeagues.data : []) || []),
  ];

  useEffect(() => {
    // Only set URL params if there are no existing college league URL parameters
    // Wait for college leagues data to be fully loaded (more than just UPCOMING_GAMES_ITEM)
    if (
      isCollegeLeagues &&
      collegeLeaguesList &&
      collegeLeaguesList.length > 1 &&
      !params?.collegeLeagueId
    ) {
      // First, check localStorage for saved league
      const lastCollegeLeagueKey = `${LOCAL_STORAGE_KEY}last-college-league`;
      const savedLeague = localStorage.getItem(lastCollegeLeagueKey);

      let leagueToUse = null;

      if (savedLeague) {
        try {
          const { label, value } = JSON.parse(savedLeague);

          // Verify this league exists in the list
          // Note: values are stored as strings in localStorage but may be numbers in the list
          const foundLeague = collegeLeaguesList.find((league) => {
            const leagueId =
              league.league_id != null ? league.league_id : league.value;
            const matches =
              leagueId != null && leagueId.toString() === value.toString();
            return matches;
          });

          if (foundLeague) {
            leagueToUse = { label, value };
          } else {
          }
        } catch (e) {
          console.error('[Header] Error parsing saved league:', e);
        }
      }

      // If no saved league or it doesn't exist, use default "Upcoming Events"
      if (!leagueToUse) {
        const defaultLeague = collegeLeaguesList.find(
          (league) =>
            (league.league_id ? league.league_id : league.value) ===
            UPCOMING_GAMES_ITEM.value
        );

        if (defaultLeague) {
          leagueToUse = {
            label: defaultLeague.league_name || defaultLeague.label,
            value: defaultLeague.league_id || defaultLeague.value,
          };

          // Save the default to localStorage for next time
          localStorage.setItem(
            lastCollegeLeagueKey,
            JSON.stringify(leagueToUse)
          );
        }
      }

      // Set URL params - this will trigger the second useEffect to load data
      if (leagueToUse) {
        setSearchParams(
          (prevValue) => {
            prevValue.set('collegeLeague', leagueToUse.label);
            prevValue.set('collegeLeagueId', leagueToUse.value);
            return prevValue;
          },
          { replace: true }
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collegeLeagues?.data, isCollegeLeagues, params?.collegeLeagueId]);

  // Load data when URL parameters are present (e.g., navigating with saved selection)
  useEffect(() => {
    if (
      isCollegeLeagues &&
      params?.collegeLeagueId &&
      collegeLeaguesList?.length > 0
    ) {
      console.log(
        '[Header] URL has collegeLeagueId, loading data for:',
        params.collegeLeague,
        params.collegeLeagueId
      );

      dispatch(allGamesResetAction());

      if (params.collegeLeagueId == UPCOMING_GAMES_ITEM.value) {
        console.log('[Header] Loading upcoming events from URL params');
        api.games
          .getCollegeUpcomingEvents({
            teamId: configuration?.one_team_id,
            leagueId: Number(params.collegeLeagueId),
          })
          .then(({ data }) => {
            const gameIndex = getIndex(data, false);
            console.log(
              '[Header] Upcoming events loaded from URL, gameIndex:',
              gameIndex
            );
            dispatch({ type: types.GET_GAMES_SUCCESS, payload: data });
            dispatch({ type: types.GET_CURRENT_GAME, payload: gameIndex });
          })
          .catch(() => {
            dispatch({
              type: types.GET_GAMES_ERROR,
              payload:
                'Something went wrong. It seems there is no info about this team schedule. Please try again later',
            });
          });
      } else {
        console.log(
          '[Header] Loading games for league from URL:',
          params.collegeLeagueId
        );
        dispatch(
          getAllGamesAction({
            teamId: configuration?.one_team_id,
            leagueId: Number(params.collegeLeagueId),
          })
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCollegeLeagues, params?.collegeLeagueId, collegeLeaguesList?.length]);

  // Auto-set URL params to "Upcoming Events" for team selection sites
  // This is the legacy behavior that makes the hack work
  useEffect(() => {
    if (
      (configuration?.display_teams_select === '1' ||
        configuration?.display_teams_select === 1) &&
      !params?.team &&
      !Boolean(savedTeam)
    ) {
      setSearchParams(
        (prevValue) => {
          if (params?.game) {
            prevValue.delete('eventId');
          }
          prevValue.set('league', configuration?.one_league_name);
          prevValue.set('leagueId', configuration?.one_league_id);
          prevValue.set('team', UPCOMING_GAMES_LABEL);
          prevValue.set('teamId', configuration?.one_league_id);
          return prevValue;
        },
        { replace: true }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configuration?.display_teams_select, params?.team]);

  if (
    isConfigurationLoading ||
    Boolean(isConfigurationError) ||
    !configuration
  ) {
    return null;
  }

  // Handle both string '1' and integer 1 from API
  const shouldShowLeagues =
    configuration?.display_leagues_select === '1' ||
    configuration?.display_leagues_select === 1;
  const isNotSelectionMade = shouldShowLeagues && isEmptyObject(params);

  return (
    <>
      {shouldShowLeagues ? (
        <SelectContainer>
          <Selector
            defaultValue={leaguesList[0]}
            onChange={handleLeaguesChange}
            options={leaguesList}
            placeholder="Select League..."
            value={getDefaultLeagueValue}
          />
          {isNotSelectionMade || !teamsOptionsList?.length ? null : (
            <StyledFlex>
              <Selector
                isDisabled={isNotSelectionMade || !teamsOptionsList?.length}
                onChange={handleTeamChange}
                options={teamsOptionsList}
                placeholder={
                  isNotSelectionMade || !teamsOptionsList?.length
                    ? ''
                    : 'Select Team...'
                }
                // placeholder="Select Team..."
                value={createSelectedValue({
                  value: params?.teamId,
                  label: params?.team,
                })}
              />
              {isUserAuth ? <AddToFavorite userId={userId} /> : null}
            </StyledFlex>
          )}
        </SelectContainer>
      ) : null}

      {configuration?.display_teams_select === '1' ||
      configuration?.display_teams_select === 1 ? (
        <SelectContainer $centered={true}>
          <Selector
            onChange={handleTeamChange}
            options={teamsOptionsList}
            placeholder="Select Team..."
            value={createSelectedValue({
              value: params?.teamId || teamsOptionsList[0].value,
              label: params?.team || teamsOptionsList[0].label,
            })}
          />
        </SelectContainer>
      ) : null}

      {isCollegeLeagues && !isCollegeLeaguesLoading ? (
        <SelectContainer $centered={true}>
          <Selector
            onChange={handleCollegeLeagueChanged}
            //options={collegeLeaguesList}
            options={collegeLeaguesList?.map((league) => ({
              label:
                league.league_name != null
                  ? league.league_name
                  : league.label != null
                    ? league.label
                    : '',
              value:
                league.league_id != null
                  ? league.league_id.toString()
                  : league.value != null
                    ? league.value.toString()
                    : -1,
            }))}
            placeholder="Select League..."
            value={createSelectedValue({
              value: params?.collegeLeagueId,
              label: params?.collegeLeague,
            })}
          />
        </SelectContainer>
      ) : null}
    </>
  );
};
